import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { fontSize, radius, spacing } from '@/constants/theme';

const { width: SCREEN_W } = Dimensions.get('window');

type Slide = {
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  title: string;
  body: string;
};

type Props = {
  visible: boolean;
  onDone: () => void;
};

export function CircleTutorial({ visible, onDone }: Props) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  const SLIDES: Slide[] = [
    { icon: 'people', iconColor: '#C9A843', title: t('circleTutorial.slide1_title'), body: t('circleTutorial.slide1_body') },
    { icon: 'git-network', iconColor: '#818CF8', title: t('circleTutorial.slide2_title'), body: t('circleTutorial.slide2_body') },
    { icon: 'shield-checkmark', iconColor: '#4ADE80', title: t('circleTutorial.slide3_title'), body: t('circleTutorial.slide3_body') },
    { icon: 'megaphone-outline', iconColor: '#C9A843', title: t('circleTutorial.slide4_title'), body: t('circleTutorial.slide4_body') },
    { icon: 'rocket', iconColor: '#F472B6', title: t('circleTutorial.slide5_title'), body: t('circleTutorial.slide5_body') },
  ];

  // Καθαρό κλικ-βασισμένο βήμα προς βήμα (χωρίς scrollToIndex σε FlatList) —
  // στο web το scrollToIndex συχνά αποτυγχάνει σιωπηλά, οπότε το "Επόμενο"
  // έμοιαζε να μην κάνει τίποτα.
  function next() {
    if (index < SLIDES.length - 1) {
      setIndex(index + 1);
    } else {
      onDone();
    }
  }

  const isLast = index === SLIDES.length - 1;
  const slide = SLIDES[index];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDone}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          {/* Slide */}
          <View style={styles.slide}>
            <View style={[styles.iconCircle, { borderColor: slide.iconColor + '40', backgroundColor: slide.iconColor + '18' }]}>
              <Ionicons name={slide.icon} size={44} color={slide.iconColor} />
            </View>
            <Text style={styles.slideTitle}>{slide.title}</Text>
            <Text style={styles.slideBody}>{slide.body}</Text>
          </View>

          {/* Dots */}
          <View style={styles.dots}>
            {SLIDES.map((_, i) => (
              <View
                key={i}
                style={[styles.dot, i === index && styles.dotActive]}
              />
            ))}
          </View>

          {/* Buttons */}
          <View style={styles.btnRow}>
            <Pressable style={styles.skipBtn} onPress={onDone} accessibilityRole="button">
              <Text style={styles.skipText}>{t('circleTutorial.skip')}</Text>
            </Pressable>
            <Pressable style={styles.nextBtn} onPress={next} accessibilityRole="button">
              <Text style={styles.nextText}>{isLast ? t('circleTutorial.gotIt') : t('circleTutorial.next')}</Text>
              {!isLast && <Ionicons name="arrow-forward" size={16} color="#fff" />}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#0F2236',
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    borderTopWidth: 1,
    borderColor: 'rgba(201,168,67,0.2)',
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
    overflow: 'hidden',
  },
  slide: {
    width: SCREEN_W,
    paddingHorizontal: spacing.xl,
    alignItems: 'center',
    gap: spacing.md,
    paddingBottom: spacing.md,
  },
  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  slideTitle: {
    color: '#FFFFFF',
    fontSize: fontSize.xl,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 28,
  },
  slideBody: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: fontSize.md,
    textAlign: 'center',
    lineHeight: 24,
  },
  dots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 7,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  dotActive: {
    backgroundColor: '#C9A843',
    width: 20,
    borderRadius: 3.5,
  },
  btnRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: spacing.lg,
    marginTop: spacing.sm,
  },
  skipBtn: {
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
  },
  skipText: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: fontSize.md,
    fontWeight: '500',
  },
  nextBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: '#C9A843',
    borderRadius: radius.full,
    paddingVertical: 12,
    paddingHorizontal: spacing.lg,
  },
  nextText: {
    color: '#0D1B2A',
    fontSize: fontSize.md,
    fontWeight: '800',
  },
});
